import type { Href } from 'expo-router';

import type { PartnerOrg, PrescribedPrograms } from '@/modules/api';
import type { User } from '@/types/user';

import { ELIGIBILITY_CHECK_FIELDS } from '../constants/eligibilityCheckFields';
import type { AuthStatus } from '../types/authStatus';
import type { EligibilityCheckResult } from '../types/eligibilityCheckResult';
import type { PartnersState } from '../types/partnersState';
import type { PurlState } from '../types/purl';
import type { Resolution, ResourceState, RoutingResource } from '../types/resolution';

import { calculateCurrentRouteFromEligibilityCheckResult } from './calculateCurrentRouteFromEligibilityCheckResult';

export type ResolverInput = {
  authStatus: AuthStatus;
  user: User | undefined;
  /** The `?org=` URL hint — consulted ONLY in the eligibility stage. */
  orgParam: string | undefined;
  /**
   * For SSO users, the bare uuid of `customerProfile.ssoPayingOrganization`.
   * When present, the eligibility-stage partners gate waits for the catalog so
   * the SSO org can be matched (and company-search skipped) rather than routing
   * to the bare search form before the catalog loads.
   */
  ssoWebserverOrgUuid?: string;
  partners: PartnersState;
  selectedOrg: PartnerOrg | undefined;
  eligibilityCheckResult: EligibilityCheckResult | undefined;
  /**
   * PURL ("Personalized URL") pre-authenticated enrollment state. When a valid
   * token is present and no account exists yet, PURL owns the pre-account stage:
   * the dedicated DOB-verification entry screen until the DOB is confirmed, then
   * account creation. Transient (lost on a cold resolve) — like
   * eligibilityCheckResult, it's derived from a URL token + in-session actions.
   */
  purl: PurlState;
  programInstances: ResourceState<boolean>; // hasProgram
  metricPoints: ResourceState<boolean>; // surveyComplete
  teams: ResourceState<boolean>; // hasCoach
  /**
   * The `onb_use_dynamic_assessment` experiment arm (test → true), loaded as a
   * resource by useOnboardingResources. Consulted only at the survey gate: the
   * test arm serves the whole assessment region as dynamic flows at
   * /step/dynamic-assessment, control walks the legacy screens from
   * /step/health-conditions. `error` fails open to control (legacy).
   */
  dynamicAssessment: ResourceState<boolean>;
  /**
   * Latched when a dynamic-assessment pass failed mid-flight (load/submit error).
   * All-or-nothing: rather than falling back to the legacy walk, an errored pass
   * abandons the assessment region entirely for the rest of the session and the
   * resolver skips straight to the program region. Transient, like
   * eligibilityCheckResult.
   */
  dynamicAssessmentErrored: boolean;
  prescribedPrograms: ResourceState<PrescribedPrograms>; // program-matching catalog
  /**
   * A PHQ/GAD result requiring the crisis line (set by `enterCrisisProgram`).
   * Transient — like Sparta's `setCrisisProgramForOverview`, it's derived from
   * the questionnaire POST response, not durable state, so it's lost on a cold
   * resolve; by then the user has either enrolled (→ coach matching) or falls
   * back to normal program matching.
   */
  requiresCrisisLine: boolean;
  /**
   * Set by `enrollInProgram` when the just-enrolled program qualifies for the
   * testimonial page (Sparta's `showTestimonialPage`). Transient — shown once
   * between enrollment and coach matching; lost on a cold resolve (→ straight to
   * coach matching, matching Sparta).
   */
  showTestimonialPage: boolean;
  /**
   * Composed by RulesEngineContext: the member-contact experiment is on its test
   * variant AND the step hasn't been dismissed this session AND the member hasn't
   * already provided address/phone. Inserts the Member Contact step after the
   * coach phase, before the terminal. All the gating is decided in the context
   * (which owns the flag read, dismissal, and the user), so the resolver just
   * honours the boolean.
   */
  showMemberContact: boolean;
};

const route = (href: Href): Resolution => ({ kind: 'route', href });
const need = (resource: RoutingResource): Resolution => ({ kind: 'need', resource });

const isPending = (r: { status: string }) => r.status === 'unset' || r.status === 'loading';
// `error` fails open to the safe default (false) — matches the prior
// `.catch(() => false)` behaviour for program/survey/teams fetches.
const readBool = (r: ResourceState<boolean>) => (r.status === 'ready' ? r.value : false);

/**
 * Profile-completeness gate, per Sparta's `checkAccountCreationEpic` (ethnicity
 * + height). Exported so callers can decide when the user has reached the
 * program-enrollment stage.
 */
export function isOnboardingProfileComplete(user?: User): boolean {
  const customerProfile = user?.customerProfile;
  return (
    typeof customerProfile?.ethnicity === 'string' &&
    customerProfile.ethnicity.length > 0 &&
    typeof customerProfile?.height === 'number' &&
    customerProfile.height > 0
  );
}

// The eligibility entry screen for a selected org. Mirrors Sparta's
// `EligibilityCheckFields` closed enum; orgs that don't support an eligibility
// check (or fall outside the set) use the invite-code form on
// /employer-or-health-plan-verification. `?org=` is only an entry hint; once
// resolved, downstream screens read the selected org from rules-engine state.
function eligibilityEntryRoute(org: PartnerOrg): Href {
  if (!org.supportsEligibilityCheck) {
    return '/step/employer-or-health-plan-verification';
  }
  switch (org.eligibilityCheckFields) {
    case ELIGIBILITY_CHECK_FIELDS.FN_LN_DOB:
    case ELIGIBILITY_CHECK_FIELDS.FN_LN_DOB_OR_EMPID_DOB:
      // CompanySearchScreen auto-fires the check from the FN/LN/DOB already in
      // the draft. /employee-search is the post-failure retry screen, only
      // reachable via calculateCurrentRouteFromEligibilityCheckResult.
      return '/step/company-search';
    case ELIGIBILITY_CHECK_FIELDS.FN_LN_DOB_AND_EMPID_OR_ZC:
      return '/step/employee-zip-verification';
    default:
      return '/step/employer-or-health-plan-verification';
  }
}

/**
 * The onboarding "manager" — the single decision-maker for navigation. A pure,
 * total function over the engine's inputs: returns either the route the user
 * belongs on, or the next async resource the driver must fetch before a route
 * can be decided. Resolution is derived from DURABLE backend state, so it's
 * identical on every entry path (cold start, refresh, repeat `?org=` email
 * deeplink) — the deeplink is only a hint, consulted in the eligibility stage.
 *
 * Mirrors Sparta's checkOnboardingProgress → checkAccountCreation →
 * checkProgramEnrollment → checkHistoricalMetricPoints / checkCoachEnrollment
 * chain, collapsed into one synchronous derivation. The driver gates on
 * `authStatus !== 'loading'`, so the resolver never sees the loading state.
 *
 * `goToNextStep` (forward nav) also calls this, then applies the survey
 * group-order override (`findNextStep`) when it parks at the survey start.
 */
export function resolveOnboardingRoute(input: ResolverInput): Resolution {
  const {
    authStatus,
    user,
    orgParam,
    ssoWebserverOrgUuid,
    partners,
    selectedOrg,
    eligibilityCheckResult,
    purl,
    programInstances,
    metricPoints,
    teams,
    prescribedPrograms,
    dynamicAssessment,
    dynamicAssessmentErrored,
    requiresCrisisLine,
    showTestimonialPage,
    showMemberContact,
  } = input;

  // Pre-account stage — no user, profile, or birthday yet.
  if (
    authStatus === 'unauthenticated' ||
    !user ||
    !user.customerProfile ||
    !user.customerProfile.birthday
  ) {
    // PURL owns the pre-account stage while its token is validating or valid: the
    // dedicated DOB-verification entry screen (which shows its own loader while
    // validating) until the DOB is confirmed, then account creation (which
    // signs up with that DOB). Every terminal status falls through to
    // /step/account-creation, where the shell decides: `invalid` /
    // `already_enrolled` (with a redirectUrl) render PurlRedirectScreen (redirect
    // to the backend URL, or the dev sign-in sheet); `locked` / `abandoned` (and a
    // network-error `invalid` without a redirectUrl, or a signed-in user's
    // `unset`) land on the normal account-creation form.
    if (purl.status === 'validating' || purl.status === 'valid') {
      return route(purl.verifiedDob ? '/step/account-creation' : '/step/purl-dob-verification');
    }
    return route('/step/account-creation');
  }

  // Eligibility celebration: a just-passed check shows /eligible until Continue
  // clears the result. Checked above the payingOrganization gate on purpose —
  // runEligibilityCheck refreshes the user on a pass (so the screen can show the
  // paying-org image), which sets payingOrganization; without this rule the
  // resolver would skip the celebration straight to create-profile.
  if (eligibilityCheckResult?.passed === true) {
    return route('/step/eligible');
  }

  // Eligibility — no payingOrganization yet. `?org=` is consulted ONLY here.
  if (!user.payingOrganization) {
    // A settled failed check decides the route (→ retry screen).
    if (eligibilityCheckResult?.passed === false) {
      return route(calculateCurrentRouteFromEligibilityCheckResult(eligibilityCheckResult));
    }
    // Need the partner catalog to hydrate `selectedOrg` from `?org=` or, for SSO
    // users, from the SSO paying org (matched by `webserverOrgUuid`). Once
    // partners are ready (matched or not) or errored, fall through — an
    // unmatched org lands on /company-search.
    const orgParamPending = Boolean(orgParam) && !selectedOrg && isPending(partners);
    const ssoPending = Boolean(ssoWebserverOrgUuid) && !selectedOrg && isPending(partners);
    if (orgParamPending || ssoPending) {
      return need('partners');
    }
    return route(selectedOrg ? eligibilityEntryRoute(selectedOrg) : '/step/company-search');
  }

  // Profile — paying org exists; finish the customer profile first.
  if (!isOnboardingProfileComplete(user)) {
    return route('/step/create-profile');
  }

  // Program enrollment (Sparta's checkProgramEnrollment).
  if (isPending(programInstances)) {
    return need('programInstances');
  }
  if (readBool(programInstances)) {
    // Coach enrollment (Sparta's checkCoachEnrollment).
    if (isPending(teams)) {
      return need('teams');
    }
    // `teams` is really a hasCoach boolean, currently derived from the solo team
    // only (likely a bug — see the TODO on `hasPrimaryCoach`). This branch is
    // routing, not the completion definition: `onboarding-completed` follows
    // `isOnboardingComplete` in RulesEngineContext (a coach on any non-chatbot
    // team), which this solo-team check can disagree with.
    if (readBool(teams)) {
      if (showMemberContact) {
        return route('/step/member-contact');
      }
      return route('/step/transition-to-native-app');
    }
    // No coach yet: the optional testimonial page comes before coach matching
    // (Sparta's TestimonialPage → checkCoachEnrollment), then coach matching.
    if (showTestimonialPage) {
      return route('/step/testimonial-page');
    }
    return route('/step/coach-matching');
  }

  // Survey gate (Sparta's checkHistoricalMetricPoints). Returns the survey
  // *start*; advancing within it (no state-derivable order) is goToNextStep's job.
  // An errored dynamic pass abandons the whole assessment region (all-or-nothing):
  // skip the gate entirely and fall through to the program region rather than
  // falling back to the legacy walk.
  if (!dynamicAssessmentErrored) {
    if (isPending(metricPoints)) {
      return need('metricPoints');
    }
    if (!readBool(metricPoints)) {
      // The survey start depends on the `onb_use_dynamic_assessment` arm: the test
      // arm serves the whole assessment region (conditions → goals → AOM → diabetes →
      // phq/gad) as dynamic flows at /step/dynamic-assessment; control (or a failed
      // flag read) walks the legacy screens from /step/health-conditions.
      // goToNextStep keys its assessment-region handling off which start comes back here.
      if (isPending(dynamicAssessment)) {
        return need('dynamicAssessment');
      }
      return route(
        readBool(dynamicAssessment) ? '/step/dynamic-assessment' : '/step/health-conditions'
      );
    }
  }

  // Crisis program (Sparta's setCrisisProgramForOverview). A PHQ/GAD result
  // requiring the crisis line routes to a dedicated screen instead of normal
  // program matching, until the user enrolls (which sets a program instance and
  // satisfies the gate above). Reached only with no program instance yet.
  if (requiresCrisisLine) {
    return route('/step/crisis-program-selection');
  }

  // Program matching (Sparta's loadPrograms → program selection). We do NOT
  // block on the prescribed-programs catalog: route into the list immediately so
  // the screen can show a skeleton while the catalog loads (the driver kicks the
  // fetch). Once it's ready, a goStraightToDetailsPage catalog re-routes to the
  // primary-recommendation detail (Sparta's prescribed / hard-recommend flow);
  // anything else (loading, error, or a normal catalog) stays on the full list,
  // which surfaces its own skeleton / empty / error state. The intra-flow order
  // (list → coaching-method → review, or detail → enroll) is selection-driven,
  // so goToNextStep owns it — this only decides the entry.
  const catalog = prescribedPrograms.status === 'ready' ? prescribedPrograms.value : undefined;
  // No matching program (Sparta's loadProgramsSuccessEpic `programs.size === 0`
  // → EmptyProgram). A ready-but-empty catalog is a dead end, not a list to
  // skeleton — route to the dedicated screen (org fallbackMessage or default
  // copy) instead of leaving the user on an empty list.
  if (catalog && catalog.programs.length === 0) {
    return route('/step/empty-program');
  }
  if (catalog?.goStraightToDetailsPage) {
    return route('/step/primary-recommendation-program-detail-screen');
  }
  return route('/step/program-matching');
}
